import { Stack } from '@mui/material';
import MuiMarkdown from 'mui-markdown';

import useTaskDocumentUpload from './useTaskDocumentUpload';

import Dialog from 'modules/common/components/MUI/Dialog';
import FileUploadArea from 'modules/common/components/FileUploadArea';
import { useDocumentTypes } from 'modules/Documents/queries/documents';
import { getDocumentTypeLabel } from 'modules/Documents/utils';
import Tag from 'modules/common/components/MUI/Tag';
import { SubmitActionTask, UploadContext } from 'modules/Tasks/types';

interface UploadModalProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
}

function isUploadContext(ctx: unknown): ctx is UploadContext {
    return typeof ctx === 'object' && ctx !== null && typeof (ctx as UploadContext).document_type_id === 'string';
}

export default function UploadModal({ open, onClose, task }: Readonly<UploadModalProps>) {
    const { data: documentTypes = [] } = useDocumentTypes();
    const context = isUploadContext(task.context) ? task.context : null;

    const {
        isUploading,
        hasStagedFile,
        uploadTrigger,
        handleSave,
        handleStagedFilesChange,
        handleUploadComplete,
        handleUploadError,
    } = useTaskDocumentUpload({ task, documentTypeId: context?.document_type_id, open, onClose });

    if (!context) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            heading={`Upload ${getDocumentTypeLabel(context.document_type_id, documentTypes)}`}
            size="medium"
            positiveAction={{
                label: 'Save',
                onClick: handleSave,
                loading: isUploading,
                disabled: isUploading || !hasStagedFile,
            }}>
            {context.notes && (
                <Stack spacing={1} sx={{ mb: 3 }}>
                    <Tag color="info" variant="filled" label="Notes" />
                    <MuiMarkdown>{context.notes}</MuiMarkdown>
                </Stack>
            )}
            <FileUploadArea
                key={`task-upload-${task.id}-${open}`}
                documentType={context.document_type_id}
                meta={{
                    document_type: context.document_type_id,
                    task_instance_id: task.id,
                    ...(context.month != null && { month: context.month }),
                    ...(context.year != null && { year: context.year }),
                    ...(context.quarter != null && { quarter: context.quarter }),
                }}
                deferUpload
                uploadTrigger={uploadTrigger}
                onUploadComplete={handleUploadComplete}
                onStagedFilesChange={handleStagedFilesChange}
                onUploadError={handleUploadError}
            />
        </Dialog>
    );
}
